import { ImportIcon, showToast } from '@bp/ui-components';
import { getFragmentData, graphql } from 'client/gql';
import { JobStatus, SortDirection } from 'client/gql/graphql';
import { BpCard } from 'components/BpCard/BpCard';
import { OrganizationConfigContext } from 'context/OrganizationConfigContext';
import dayjs from 'dayjs';
import { useAuthClaims } from 'hooks/useAuthClaims';
import { useConfirm } from 'hooks/useConfirm';
import { SettingsImportPlanungSubpageOutletContext } from 'pages/Settings/subpages/SettingsImportPlanungSubpage';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext } from 'react-router-dom';
import { useMutation, useQuery } from 'urql';
import { backendApi } from 'utils/backendApi';
import { SyncMaster } from 'utils/create-foreignRefId';
import { EventJobsTableFragment, PlanungImportEventsJobTable } from '../tables/PlanungImportEventsJobTable';
import { PlanungImportEventsTable } from '../tables/PlanungImportEventsTable';

const PlanungImportEventsFormDocument = graphql(`
  query PlanungImportEventsFormQuery(
    $eventWhere: CalendarEventWhere
    $eventOptions: CalendarEventOptions
    $eventJobWhere: EventJobWhere
    $eventJobOptions: EventJobOptions
  ) {
    ...PlanungImportEventsTableFields
    ...EventJobsTableFields
  }
`);

const PlanungCancelEventJobDocument = graphql(`
  mutation PlanungCancelSynchronisationEvent($uuid: ID!, $update: EventJobUpdateInput) {
    updateEventJobs(where: { uuid: $uuid }, update: $update) {
      eventJobs {
        uuid
      }
    }
  }
`);

export const PlanungImportEventsFormForm = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const { currentSchoolYear } = useContext(OrganizationConfigContext);

  const { planungSchoolyear } = useOutletContext<SettingsImportPlanungSubpageOutletContext>();

  const { ConfirmationDialog, confirm } = useConfirm({
    defaultTitle: t('planungImport.importEvents'),
    defaultMessage: t('planungImport.importEventsMessage'),
  });

  const [, cancelEventJob] = useMutation(PlanungCancelEventJobDocument);

  const [loading, setLoading] = useState<boolean>(false);

  const daysSome = {
    year_LT: dayjs(currentSchoolYear.start).year() + 2,
    year_GT: dayjs(currentSchoolYear.start).year() - 1,
  };

  const [{ data }, fetch] = useQuery({
    query: PlanungImportEventsFormDocument,
    variables: {
      eventWhere: {
        days_SOME: daysSome,
        prodId: SyncMaster.planung,
        organization: { uuid: organizationUuid },
      },
      eventOptions: {
        sort: [{ start: SortDirection.Asc }],
      },
      eventJobWhere: {
        organizationUuid,
      },
      eventJobOptions: {
        sort: [{ createdAt: SortDirection.Desc }],
      },
    },
    pause: !currentSchoolYear,
  });

  const eventJobsFragment = getFragmentData(EventJobsTableFragment, data);
  const hasRunningJobs = useMemo(() => {
    const hasRunningJobs = eventJobsFragment?.eventJobs.some((e) => e.status === JobStatus.Running);

    if (hasRunningJobs) {
      const intervalId = setInterval(() => {
        fetch({ requestPolicy: 'network-only' });
      }, 10_000);

      setTimeout(() => {
        clearInterval(intervalId);
      }, 1200000);
    }

    return hasRunningJobs;
  }, [eventJobsFragment, fetch]);

  async function importEvents() {
    await backendApi.post(
      `/planung/import/events`,
      JSON.stringify({
        organizationUuid,
        planungSchoolYear: planungSchoolyear,
        from: currentSchoolYear.start,
        to: currentSchoolYear.end,
      }),
    );
  }

  async function handleImport() {
    if (currentSchoolYear) {
      showToast(t('planungImport.importStarted'), {
        autoClose: 1200000,
        type: 'success',
        showCloseButton: false,
        onClose: () => {
          fetch();
        },
      });

      const intervalId = setInterval(() => {
        fetch({ requestPolicy: 'network-only' });
      }, 10_000);

      setTimeout(() => {
        clearInterval(intervalId);
      }, 1200000);

      setLoading(true);
      await importEvents();
      setLoading(false);
    }
  }

  async function dangerHandleCancelEventJob(jobUuid: string) {
    await cancelEventJob({ uuid: jobUuid, update: { status: JobStatus.Cancelled } });
    fetch({ requestPolicy: 'network-only' });
  }

  return (
    <div>
      <BpCard
        noPadding
        header={{
          headline: t('planungImport.eventJobs'),
          actions: [
            {
              text: t('planungImport.createEvents'),
              callback: async () => {
                const result = await confirm();
                if (result) await handleImport();
              },
              hierarchy: 'primary',
              icon: <ImportIcon />,
              disabled: loading || hasRunningJobs,
            },
          ],
        }}
      >
        <PlanungImportEventsJobTable query={data} dangerHandleCancelClick={dangerHandleCancelEventJob} />
      </BpCard>

      <BpCard className='mt-6' noPadding header={{ headline: t('planungImport.importedPlanungEvents') }}>
        <PlanungImportEventsTable query={data} />
      </BpCard>

      <ConfirmationDialog />
    </div>
  );
};
